/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Account,
  AccountFilterModel,
  cacheKeys,
  getFullName,
  useAccountFilter,
  useCurrentAccount,
  useCustomMutation,
  useImpersonateUser,
  useRoles,
  useSetAccountFilter,
} from '@gripp/shared-logic';
import {
  BaseColumnCheckbox,
  WorkspaceColumn,
  getAccountColumn,
  getFormattedPhoneNumber,
  getGroupColumn,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text } from 'react-native';
import { Button } from 'react-native-paper';

export const useColumnsAccount = (
  showCheckboxColumn: boolean,
  showRolesColumn: boolean,
  showImpersonateWorkspace: boolean,
  showImpersonateUser: boolean,
  showGlobalAdminRole: boolean,
  workspaceId?: string
) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();
  const [columns, setColumns] = useState<ColumnDef<unknown, never>[]>([]);
  const queryClient = useQueryClient();
  const { data: accountFilter } = useAccountFilter();
  const { mutateAsync: setAccountFilter } = useSetAccountFilter();
  const { roles } = useRoles();
  const { account: currentAccount } = useCurrentAccount();

  const { impersonateUserAsync: impersonateUser } = useImpersonateUser();
  const { mutateAsync: removeFromWorkspace } = useCustomMutation({
    query: `
      mutation RemoveFromWorkspace($input: RemoveFromWorkspaceInput!) {
        account {
          removeFromWorkspace(input: $input) {
            success
          }
        }
      }`,
  });

  const { mutateAsync: updateAccountType } = useCustomMutation({
    query: `
      mutation ChangeRole($input: ChangeRoleInput!) {
        account {
          changeRole(input: $input) {
            id
          }
        }
      }`,
  });

  // Get current user's role name
  const currentUserRole = useMemo(() => {
    if (
      !currentAccount?.workspaceRoles ||
      currentAccount.workspaceRoles.length === 0 ||
      !workspaceId
    ) {
      return undefined;
    }

    // Find the role for the current workspace
    const workspaceRole = currentAccount.workspaceRoles.find(
      (wr) => wr.workspace === workspaceId
    );

    if (!workspaceRole) {
      return undefined;
    }

    const currentRole = roles.find((r) => r.id === workspaceRole.role);

    return currentRole?.name;
  }, [currentAccount, roles, workspaceId]);

  const updateGroupFilter = async (
    groupId: string,
    accountFilter: AccountFilterModel,
    disabled = false
  ) => {
    if (!disabled)
      await setAccountFilter({
        ...accountFilter,
        groups: [groupId],
      });
  };

  const allColumns: Record<string, ColumnDef<unknown, never>> = useMemo(
    () => ({
      selected: columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      name: columnHelper.accessor('name', {
        cell: (info) => {
          const name = info.getValue();
          return <Text style={styles.cellText}>{getFullName(name)}</Text>;
        },
        header: () => t('account.list.name'),
        meta: {
          sortable: true,
          exportCell: (data) => `${data.name?.first} ${data.name?.last}`,
        },
      }),
      jobTitle: columnHelper.accessor('jobTitle', {
        cell: (info) => <Text>{info.getValue()}</Text>,
        header: () => t('account.list.jobTitle'),
        meta: { sortable: true },
      }),
      email: columnHelper.accessor('email', {
        cell: (info) => <Text style={styles.emailText}>{info.getValue()}</Text>,
        header: () => t('account.list.email'),
        meta: { sortable: true },
      }),
      phone: columnHelper.accessor('phone', {
        cell: (info) => {
          const phone = info.getValue() as any;
          if (phone?.phoneNumber) {
            const phoneNumber = getFormattedPhoneNumber(
              phone.countryCode,
              phone.callingCode,
              phone.phoneNumber
            );
            return <Text style={styles.emailText}>{phoneNumber}</Text>;
          }
          return null;
        },
        header: () => t('account.list.phone'),
        meta: {
          sortable: true,
          exportCell: (data) => {
            const phone = data.phone;
            if (!phone?.phoneNumber) return '';
            return getFormattedPhoneNumber(
              phone.countryCode,
              phone.callingCode,
              phone.phoneNumber
            );
          },
        },
      }),
      roles: columnHelper.accessor('workspaceRoles', {
        cell: (info) =>
          getAccountColumn(
            workspaceId,
            info,
            roles,
            showGlobalAdminRole,
            async ({ id, action, selectedRoles }) => {
              if (action === 'remove') {
                await removeFromWorkspace({
                  input: {
                    id,
                    workspaceId: workspaceId,
                  },
                });
              } else {
                await updateAccountType({
                  input: {
                    id,
                    workspaceId: workspaceId,
                    role: selectedRoles[0],
                  },
                });
              }
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('account').all,
              });
            },
            currentUserRole
          ),
        header: () => t('account.list.accountType'),
        meta: {
          sortable: false,
          exportCell: (data: {
            workspaceRoles?: Array<{ role: string; workspace: string }>;
          }) =>
            data.workspaceRoles
              ?.filter(
                (workspaceRole) => workspaceRole.workspace === workspaceId
              )
              .map((workspaceRole) => {
                const matchingRole = roles.find(
                  (r) => r.id === workspaceRole.role
                );
                return matchingRole ? matchingRole.name : workspaceRole.role;
              })
              .join(', ') || '',
        },
      }),
      groups: columnHelper.accessor('groups', {
        cell: (info) =>
          getGroupColumn(
            info,
            async (groupId) =>
              await updateGroupFilter(groupId, accountFilter, true)
          ),
        header: () => t('account.list.groups'),
        meta: {
          sortable: false,
          exportCell: (data) =>
            data.groups?.map((group) => group.name).join(', ') || '',
        },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          if (info.getValue()) {
            const date = moment(info.getValue());
            return <Text>{date.fromNow()}</Text>;
          }
        },
        header: () => t('account.list.added'),
        meta: {
          sortable: true,
          exportable: false, // Hide from export
        },
      }),
      createdDate: columnHelper.accessor('createdDate', {
        cell: () => null, // Hidden column for export only
        header: () => t('account.list.createdDate'),
        meta: {
          sortable: false,
          exportCell: (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt).toISOString().split('T')[0];
          },
          hidden: true, // Hidden from grid display
        },
      }),
      createdTime: columnHelper.accessor('createdTime', {
        cell: () => null, // Hidden column for export only
        header: () => t('account.list.createdTime'),
        meta: {
          sortable: false,
          exportCell: (data: any) => {
            if (!data.createdAt) return '';
            return new Date(data.createdAt)
              .toISOString()
              .split('T')[1]
              .substring(0, 8);
          },
          hidden: true, // Hidden from grid display
        },
      }),
      workspace: columnHelper.accessor('workspace', {
        cell: (info) => {
          const workspaces = info.row.original.workspace.map((ws, i) => {
            return {
              id: info.row.original.workspaceRoles[i].workspace,
              name: ws.name,
            };
          });

          return (
            <WorkspaceColumn
              canImpersonate={showImpersonateWorkspace}
              workspaces={workspaces as any[]}
            />
          );
        },
        header: () => t('account.list.workspace'),
        meta: {
          sortable: true,
          exportCell: (data) =>
            data.workspace?.map((workspace) => workspace.name).join(', ') || '',
        },
      }),
      impersonate: columnHelper.accessor('impersonate', {
        cell: ({ row }) => {
          const original = row.original as Account;
          const onPress = async () => {
            const input = {
              accountId: original.id,
            };
            impersonateUser(input, () => {
              window.location.href = '/activities';
            });
          };
          return <Button onPress={onPress}>Impersonate</Button>;
        },
        header: () => '',
        meta: { sortable: false },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountFilter, roles, showGlobalAdminRole]
  );

  useEffect(() => {
    if (!accountFilter) return;

    const columnList = [];
    if (showCheckboxColumn) {
      columnList.push(allColumns.selected);
    }

    columnList.push(allColumns.name);

    Object.keys(allColumns)
      .filter(
        (key) =>
          accountFilter.columns?.includes(key) &&
          !(key === 'roles' && !showRolesColumn) &&
          !(key === 'workspace' && showRolesColumn)
      )
      .forEach((key) => columnList.push(allColumns[key]));

    // Automatically add split datetime columns when createdAt is selected
    if (accountFilter.columns?.includes('createdAt')) {
      columnList.push(allColumns.createdDate);
      columnList.push(allColumns.createdTime);
    }

    if (showImpersonateUser) {
      columnList.push(allColumns['impersonate']);
    }

    // Filter out hidden columns from grid display but keep them for export
    const gridColumns = columnList.filter(
      (column) => !(column.meta as any)?.hidden
    );
    setColumns(gridColumns);
  }, [
    accountFilter,
    allColumns,
    showCheckboxColumn,
    showRolesColumn,
    showImpersonateUser,
  ]);

  return columns;
};

const styles = StyleSheet.create({
  accountsContainer: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
  emailText: {
    overflow: 'hidden',
  },
});
